import React, { useCallback, useEffect, useState } from 'react';
import {
  Trophy, TrendingUp, TrendingDown, Minus, Flame, Target,
  ArrowUpRight, Info, EyeOff,
} from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  fetchLeaderboard, VIEWPOINTS,
  type LeaderboardEntry, type LeaderboardPayload, type Metric, type Period, type Viewpoint,
} from '../../data/leaderboardMock';

/**
 * The JV Portal splash page.
 *
 * Three zones, in this order, because a public board that only ranks closings
 * shows most students an empty result every single day:
 *
 *   1. Your trajectory - personal, always has a number, does not depend on
 *      anyone else's performance.
 *   2. The board       - top ten. Aspirational, and earned.
 *   3. Your slice      - your rank and immediate neighbours, so the next
 *      position is visibly catchable rather than abstract.
 *
 * Ranking metric is selectable rather than fixed. Ranking on raw submissions
 * is deliberately NOT offered: whatever this page ranks is what students will
 * produce more of, and submissions are the one metric that can be inflated
 * without doing any real work. "Deals advanced" is the dense, frequently-moving
 * alternative, and it can only be earned by a deal a human moved forward.
 */

interface LeaderboardViewProps {
  /** Preview override. In the app the viewer comes from the session. */
  viewpoint?: Viewpoint;
  /** Whether dollar figures are visible to everyone. Unresolved product decision. */
  showEarnings?: boolean;
  /** Renders the preview-only controls. */
  previewControls?: boolean;
}

const PERIODS: { id: Period; label: string }[] = [
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: '90 Days' },
  { id: 'all', label: 'All Time' },
];

const METRICS: { id: Metric; label: string; hint: string }[] = [
  { id: 'closed', label: 'Closings', hint: 'Deals that reached closed' },
  { id: 'advanced', label: 'Deals Advanced', hint: 'Forward stage moves — the number that changes weekly' },
  { id: 'earnings', label: 'Earnings', hint: 'Student share of closed deals' },
];

function money(n: number): string {
  return `$${n.toLocaleString()}`;
}

function Trend({ value }: { value: number }) {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400">
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
      up ? 'text-emerald-600' : 'text-gray-400'
    }`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(value)}
    </span>
  );
}

const MEDALS = ['🥇', '🥈', '🥉'];

function Row({ entry, metric, showEarnings }: {
  entry: LeaderboardEntry;
  metric: Metric;
  showEarnings: boolean;
}) {
  const value =
    metric === 'closed' ? entry.closed
    : metric === 'earnings' ? entry.earnings
    : entry.advanced;

  return (
    <div
      className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-colors ${
        entry.isYou
          ? 'bg-tdb-accent/10 ring-2 ring-tdb-accent'
          : 'hover:bg-gray-50'
      }`}
    >
      <div className="w-10 shrink-0 text-center">
        {entry.rank <= 3 ? (
          <span className="text-xl leading-none">{MEDALS[entry.rank - 1]}</span>
        ) : (
          <span className={`text-sm font-bold ${entry.isYou ? 'text-tdb-accent' : 'text-gray-400'}`}>
            {entry.rank}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-semibold truncate ${entry.isYou ? 'text-tdb-accent' : 'text-gray-900'}`}>
            {entry.displayName}
          </span>
          {entry.tenureWeeks < 12 && (
            <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5
                             rounded bg-emerald-100 text-emerald-700 shrink-0">
              New
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {entry.submitted} submitted
          {entry.underContract > 0 && ` · ${entry.underContract} under contract`}
          {entry.closed > 0 && ` · ${entry.closed} closed`}
        </div>
      </div>

      <div className="hidden sm:block w-16 text-right">
        <Trend value={entry.trend} />
      </div>

      <div className="w-28 text-right shrink-0">
        <div className="text-lg font-bold text-tdb-primary tabular-nums">
          {metric === 'earnings'
            ? (showEarnings ? money(value) : '—')
            : value}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, previous, accent, hint }: {
  label: string;
  value: number | string;
  previous?: number;
  accent?: boolean;
  hint?: string;
}) {
  const numeric = typeof value === 'number' ? value : null;
  const delta = numeric !== null && previous !== undefined ? numeric - previous : null;
  const showDelta = delta !== null && delta !== 0;

  return (
    <div className={`rounded-xl p-4 ${accent ? 'bg-tdb-accent/10' : 'bg-white/10'}`}>
      <p className={`text-xs uppercase tracking-wide font-semibold ${
        accent ? 'text-tdb-accent' : 'text-gray-300'
      }`}>
        {label}
      </p>
      <p className="text-3xl font-bold text-white mt-1 tabular-nums">{value}</p>
      {showDelta && (
        <p className={`text-xs mt-1 font-medium ${delta! > 0 ? 'text-emerald-400' : 'text-gray-400'}`}>
          {delta! > 0 ? '+' : ''}{delta} vs last period
        </p>
      )}
      {hint && (
        <p className={`text-xs text-gray-400 tabular-nums ${showDelta ? 'mt-0.5' : 'mt-1'}`}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * Where the opt-out preference lives until a backend field exists. Kept in
 * lockstep with the key in SettingsView so the two components stay wired
 * up without needing a shared context for one boolean.
 */
const LEADERBOARD_OPTOUT_KEY = 'tdb.leaderboardOptOut';

function readOptOut(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(LEADERBOARD_OPTOUT_KEY) === 'true';
}

export function LeaderboardView({
  viewpoint: initialViewpoint,
  showEarnings = true,
  previewControls = false,
}: LeaderboardViewProps) {
  const [period, setPeriod] = useState<Period>('month');
  const [metric, setMetric] = useState<Metric>('closed');
  const [viewpoint, setViewpoint] = useState<Viewpoint>(initialViewpoint ?? 'mid');
  const [earningsVisible, setEarningsVisible] = useState(showEarnings);

  // Non-preview mode honours the Settings opt-out toggle. Preview mode drives
  // opted-out through the viewpoint picker so designs can be judged in both
  // states without touching localStorage.
  const [settingOptedOut, setSettingOptedOut] = useState<boolean>(
    () => !previewControls && readOptOut(),
  );
  useEffect(() => {
    if (previewControls) return;
    const onChange = (e: Event) => {
      setSettingOptedOut(Boolean((e as CustomEvent<boolean>).detail));
    };
    window.addEventListener('tdb:leaderboard-optout-changed', onChange);
    return () => window.removeEventListener('tdb:leaderboard-optout-changed', onChange);
  }, [previewControls]);

  const effectiveViewpoint: Viewpoint =
    !previewControls && settingOptedOut ? 'hidden' : viewpoint;

  const [data, setData] = useState<LeaderboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!idToken || !apiUrl) {
        setLoadError(true);
        setLoading(false);
        return;
      }
      const payload = await fetchLeaderboard(apiUrl, idToken, period, metric);
      setData(payload);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [period, metric]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
        Loading leaderboard…
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
        Unable to load leaderboard data.
      </div>
    );
  }

  const { you } = data;
  const hasClosings = you.closed > 0;

  return (
    <div className="space-y-6">
      {previewControls && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-wrap
                        items-center gap-4 text-sm">
          <span className="font-bold text-amber-900 flex items-center gap-1.5">
            <Info className="w-4 h-4" /> Preview
          </span>
          {VIEWPOINTS.map((v) => (
            <button
              key={v.id}
              onClick={() => setViewpoint(v.id)}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                viewpoint === v.id
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-amber-900 hover:bg-amber-100'
              }`}
            >
              {v.label}
            </button>
          ))}
          <label className="flex items-center gap-2 ml-auto text-amber-900 font-medium">
            <input
              type="checkbox"
              checked={earningsVisible}
              onChange={(e) => setEarningsVisible(e.target.checked)}
              className="rounded"
            />
            Show dollar figures publicly
          </label>
        </div>
      )}

      {/* ---- Zone 1: your trajectory ---------------------------------- */}
      <div className="bg-gradient-to-r from-tdb-primary via-tdb-secondary to-tdb-primary
                      rounded-2xl shadow-xl p-6 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-sm text-gray-300 font-medium uppercase tracking-wide">
              {you.optedOut ? 'Your progress' : 'Your position'}
            </p>
            {you.optedOut ? (
              <div className="flex items-center gap-3 mt-1">
                <span className="inline-flex items-center gap-2 bg-white/10 rounded-lg
                                 px-3 py-1.5 text-sm font-semibold">
                  <EyeOff className="w-4 h-4" />
                  Hidden from the board
                </span>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'settings' }))}
                  className="text-sm text-tdb-accent hover:underline font-medium"
                >
                  Manage in Settings
                </button>
              </div>
            ) : (
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-5xl font-bold">#{you.rank}</span>
                <span className="text-gray-300">of {you.totalStudents}</span>
                {you.trend !== 0 && (
                  <span className={`text-sm font-semibold ${
                    you.trend > 0 ? 'text-emerald-400' : 'text-gray-400'
                  }`}>
                    {you.trend > 0 ? `▲ up ${you.trend}` : `▼ down ${Math.abs(you.trend)}`}
                  </span>
                )}
              </div>
            )}
            {you.nextRankGap && !you.optedOut && (
              <p className="text-sm text-tdb-accent mt-2 font-medium flex items-center gap-1">
                <ArrowUpRight className="w-4 h-4" />
                {you.nextRankGap}
              </p>
            )}
          </div>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'jv-portal' }))}
            className="bg-tdb-accent hover:bg-tdb-accent-dark text-white px-5 py-2.5 rounded-xl
                       font-semibold transition-colors shrink-0"
          >
            Submit a deal
          </button>
        </div>

        {/* The pipeline the student cares about, in the order it actually
            flows. Potential value is the accent — it's what motivates. Each
            money-bearing stage carries its own dollar hint so students can
            see the value pooling as deals move forward. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile
            label="Submitted"
            value={you.submitted}
            previous={you.previous.submitted}
            hint={earningsVisible && you.submitted > 0
              ? `${money(you.submittedValue)} total if they close`
              : undefined}
          />
          <StatTile
            label="Under contract"
            value={you.underContract}
            previous={you.previous.underContract}
            hint={earningsVisible
              ? `${money(you.underContractValue)} if they close`
              : undefined}
          />
          <StatTile
            label="Closed"
            value={you.closed}
            previous={you.previous.closed}
            hint={earningsVisible && you.earnings > 0
              ? `${money(you.earnings)} earned`
              : undefined}
          />
          <StatTile
            label="Potential"
            value={earningsVisible ? money(you.potentialValue) : '—'}
            accent
            hint="If every live deal closed"
          />
        </div>

        {!hasClosings && (
          <p className="text-sm text-gray-300 mt-4 flex items-center gap-2">
            <Flame className="w-4 h-4 text-tdb-accent shrink-0" />
            {you.inFlight > 0
              ? `${you.inFlight} deal${you.inFlight === 1 ? '' : 's'} still live. Closings show here the moment one lands.`
              : 'No live deals yet — submit one to get on the board.'}
          </p>
        )}

        {hasClosings && earningsVisible && (
          <p className="text-sm text-gray-300 mt-4">
            Earned so far: <span className="text-white font-semibold">{money(you.earnings)}</span>
            <span className="text-gray-400"> · JV split is 30% of the wholesale fee</span>
          </p>
        )}
      </div>

      {/* ---- Controls -------------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {METRICS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              title={m.hint}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                metric === m.id
                  ? 'bg-white text-tdb-primary shadow'
                  : 'text-gray-500 hover:text-tdb-primary'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                period === p.id
                  ? 'bg-white text-tdb-primary shadow'
                  : 'text-gray-500 hover:text-tdb-primary'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---- Zone 2: the board ----------------------------------------- */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-tdb-primary flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            {METRICS.find((m) => m.id === metric)?.label}
            <span className="text-sm font-normal text-gray-400">
              · {PERIODS.find((p) => p.id === period)?.label}
            </span>
          </h2>
          <span className="text-xs text-gray-400">
            {METRICS.find((m) => m.id === metric)?.hint}
          </span>
        </div>

        <div className="space-y-1">
          {data.board.map((entry) => (
            <Row key={entry.studentId} entry={entry} metric={metric} showEarnings={earningsVisible} />
          ))}
        </div>

        {/* ---- Zone 3: your slice -------------------------------------- */}
        {data.slice.length > 0 && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 border-t border-dashed border-gray-300" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide
                               flex items-center gap-1">
                <Target className="w-3 h-3" /> Around you
              </span>
              <div className="flex-1 border-t border-dashed border-gray-300" />
            </div>
            <div className="space-y-1">
              {data.slice.map((entry) => (
                <Row key={entry.studentId} entry={entry} metric={metric} showEarnings={earningsVisible} />
              ))}
            </div>
          </>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center">
        Updated {new Date(data.generatedAt).toLocaleString()} · Rankings exclude
        imported and admin-created records
      </p>
    </div>
  );
}
